import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon, AlertTriangleIcon, XIcon, GlobeIcon } from 'lucide-react';
import { I18nProvider, useI18n } from '../config/i18n';
import { PTPData, StepHazardData, StepHazardItem, GeneralHazardItem } from '../types/ptp';
import { StepIndicator } from '../components/ui/StepIndicator';
import { LoadingOverlay } from '../components/ui/LoadingOverlay';
import { NewPTPScreen } from '../components/screens/NewPTPScreen';
import { VoiceDescriptionScreen } from '../components/screens/VoiceDescriptionScreen';
import { TaskUnderstandingScreen } from '../components/screens/TaskUnderstandingScreen';
import { EquipmentScreen } from '../components/screens/EquipmentScreen';
import { PermitsScreen } from '../components/screens/PermitsScreen';
import { TaskConditionsIntroScreen } from '../components/screens/TaskConditionsIntroScreen';
import { WorkEnvironmentScreen } from '../components/screens/WorkEnvironmentScreen';
import { CrewAssignmentScreen } from '../components/screens/CrewAssignmentScreen';
import { HazardReviewScreen } from '../components/screens/HazardReviewScreen';
import { StepHazardScreen } from '../components/screens/StepHazardScreen';
import { GeneralHazardsScreen } from '../components/screens/GeneralHazardsScreen';
import { PTPSummaryScreen } from '../components/screens/PTPSummaryScreen';
import { Screen10_PTPComplete } from '../components/screens/Screen10_PTPComplete';
import { Screen11_PTPCreated } from '../components/screens/Screen11_PTPCreated';
import { Screen12_Recording } from '../components/screens/Screen12_Recording';
import { Screen13_RecordingComplete } from '../components/screens/Screen13_RecordingComplete';
import { Screen14_QualityScore } from '../components/screens/Screen14_QualityScore';
import { Screen15_NewInfo } from '../components/screens/Screen15_NewInfo';
import { Screen16_UpdatedPTP } from '../components/screens/Screen16_UpdatedPTP';
import { Screen17_FullReview } from '../components/screens/Screen17_FullReview';
import { Screen18_SendQuiz } from '../components/screens/Screen18_SendQuiz';
import { Screen19_ForemanQuiz } from '../components/screens/Screen19_ForemanQuiz';
import { Screen18_CrewQuizResults } from '../components/screens/Screen18_CrewQuizResults';
import { Screen21_PTPList } from '../components/screens/Screen21_PTPList';
import { PTPExportView } from '../components/PTPExportView';
import { AppHeader } from '../components/ui/AppHeader';
import { HAZARD_IDENTIFICATION_PROMPT, CONTROLS_GENERATION_PROMPT, CONDITIONS_GENERATION_PROMPT, NEARBY_TASK_PROMPT } from '../config/aiPrompts';
import { getSavedApiKey, chatCompletionWithRetry } from '../config/openai';
const INITIAL_DATA: PTPData = {
  project: '',
  location: '',
  date: new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }),
  taskTitle: '',
  taskDescription: '',
  steps: [],
  stepHazardData: [],
  generalHazards: [],
  generalEquipment: [],
  jobSteps: [],
  taskEquipment: [],
  permits: [],
  environmentConditions: [],
  crewSize: 0,
  crewRoles: [],
  selectedHazards: [],
  riskAssessments: [],
  engineeringControls: [],
  adminControls: [],
  ppeControls: [],
  equipment: [],
  safetyChecks: [],
  _workConditionsCache: undefined
};
const STEP_LABELS = ['PTP List', 'New PTP', 'Voice Description', 'Task Steps', 'Equipment', 'Permits', 'Conditions Intro', 'Conditions', 'Crew', 'Hazards', 'Risk Assessment', 'Controls', 'Safety Checks', 'Summary', 'PTP Created', 'Recording', 'Analysis', 'Quality Score', 'New Info', 'Updated PTP', 'Meeting Review', 'Send Quiz', 'Foreman Quiz', 'Crew Readiness', 'PTP List'];
export function PTPAppInner() {
  const {
    lang,
    setLang,
    t
  } = useI18n();
  const [currentStep, setCurrentStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [data, setData] = useState<PTPData>(INITIAL_DATA);
  const [showExport, setShowExport] = useState(false);
  const [exportIsUpdated, setExportIsUpdated] = useState(false);
  const [loading, setLoading] = useState<{
    message: string;
    sub?: string;
  } | null>(null);
  // Recording state
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [isRecordingPaused, setIsRecordingPaused] = useState(false);
  // Nearby concurrent task alert
  const [nearbyTaskAlert, setNearbyTaskAlert] = useState<{
    nearbyTask: string;
    location: string;
    concern: string;
  } | null>(null);
  const [nearbyTaskFetched, setNearbyTaskFetched] = useState(false);
  const [hasShownNewInfo, setHasShownNewInfo] = useState(false);
  // Hazard generation error state
  const [hazardGenError, setHazardGenError] = useState<string | null>(null);
  const [pendingNearbyCondition, setPendingNearbyCondition] = useState<{
    id: string;
    text: string;
    relevant: boolean | null;
    category: 'E' | 'C' | 'M' | null;
  } | null>(null);
  const numHazardSteps = data.stepHazardData.length;
  // Base is now 10 (was 9) because we added Permits screen
  const TOTAL_STEPS = 10 + numHazardSteps + 1 + 1 + 12;
  const recordingStep = 10 + numHazardSteps + 2 + 3;
  // Helper: find step indices that have at least one relevant hazard
  // Step hazard screens now start at 11 (was 10)
  const findNextRelevantStepScreen = (afterScreenStep: number): number | null => {
    const startIdx = afterScreenStep <= 10 ? 0 : afterScreenStep - 11 + 1;
    for (let i = startIdx; i < data.stepHazardData.length; i++) {
      if (data.stepHazardData[i].hazards.some(h => h.relevant)) {
        return 11 + i;
      }
    }
    return null;
  };
  const findPrevRelevantStepScreen = (beforeScreenStep: number): number | null => {
    const endIdx = beforeScreenStep - 11 - 1;
    for (let i = endIdx; i >= 0; i--) {
      if (data.stepHazardData[i].hazards.some(h => h.relevant)) {
        return 11 + i;
      }
    }
    return null;
  };
  const getLoadingTransition = useCallback((fromStep: number): {
    message: string;
    sub?: string;
    duration: number;
  } | null => {
    if (fromStep === 3) return {
      message: t('loading.analyzingTask'),
      sub: t('loading.analyzingTaskSub'),
      duration: 2200
    };
    if (fromStep === 10 + numHazardSteps + 1) return {
      message: t('loading.analyzingPTP'),
      sub: t('loading.analyzingPTPSub'),
      duration: 2800
    };
    if (fromStep === 10 + numHazardSteps + 2) return {
      message: t('loading.creatingPTP'),
      sub: t('loading.creatingPTPSub'),
      duration: 2400
    };
    if (fromStep === 10 + numHazardSteps + 2 + 3) return {
      message: t('loading.extractingInfo'),
      sub: t('loading.extractingInfoSub'),
      duration: 1800
    };
    if (fromStep === 10 + numHazardSteps + 2 + 4) return {
      message: t('loading.analyzingMeeting'),
      sub: t('loading.analyzingMeetingSub'),
      duration: 2200
    };
    if (fromStep === 10 + numHazardSteps + 2 + 5) return {
      message: t('loading.updatingPTP'),
      sub: t('loading.updatingPTPSub'),
      duration: 2000
    };
    if (fromStep === 10 + numHazardSteps + 2 + 11) return {
      message: t('loading.finalizingPTP'),
      sub: t('loading.finalizingPTPSub'),
      duration: 1200
    };
    return null;
  }, [numHazardSteps, t]);
  // ── Phase 1: Generate hazard LIST only ──
  const generateHazardsList = useCallback(async () => {
    setHazardGenError(null);
    const apiKey = getSavedApiKey();
    if (!apiKey || data.jobSteps.length === 0) {
      const reason = !apiKey ? 'No OpenAI API key found' : 'No job steps defined';
      console.warn(`[AI Hazards] Skipped: ${reason}`);
      setHazardGenError(reason);
      const fallbackData: StepHazardData[] = data.jobSteps.map((step, i) => ({
        description: step,
        hazards: [{
          id: `h-fallback-${i}`,
          hazard: 'Identify hazard for this step',
          source: 'Assess the source of the hazard',
          ecmDriver: 'E' as const,
          controls: {
            eliminateSubstitute: '',
            engineering: '',
            adminTraining: '',
            ppe: ''
          },
          equipment: [],
          relevant: true
        }],
        affectedConditions: []
      }));
      setData(prev => ({
        ...prev,
        stepHazardData: fallbackData,
        generalHazards: [],
        generalEquipment: []
      }));
      return;
    }
    try {
      const jobStepsText = data.jobSteps.map((s, i) => `${i + 1}. ${s}`).join('\n');
      const conditionsText = data.environmentConditions.length > 0 ? `\n\nRelevant Conditions:\n${data.environmentConditions.map(c => `- ${c}`).join('\n')}` : '';
      const completion = await chatCompletionWithRetry({
        model: 'gpt-4o-mini',
        messages: [{
          role: 'system',
          content: HAZARD_IDENTIFICATION_PROMPT
        }, {
          role: 'user',
          content: `Task: ${data.taskDescription}\n\nJob Steps:\n${jobStepsText}${conditionsText}`
        }],
        temperature: 0.3,
        max_tokens: 1500
      });
      const raw = completion.choices[0]?.message?.content?.trim() || '';
      let jsonStr = raw;
      const fenceMatch = raw.match(/```(?:json)?\s*([\s\S]*?)```/);
      if (fenceMatch) jsonStr = fenceMatch[1].trim();
      const parsed = JSON.parse(jsonStr);
      if (!parsed.stepHazards || !Array.isArray(parsed.stepHazards)) {
        throw new Error('Invalid response structure');
      }
      let hazardCounter = 0;
      const stepHazardData: StepHazardData[] = parsed.stepHazards.map((step: any) => ({
        description: step.description,
        description_es: step.description_es,
        hazards: (step.hazards || []).map((h: any): StepHazardItem => ({
          id: `h-ai-${++hazardCounter}`,
          hazard: h.hazard || 'Unknown hazard',
          source: h.source || 'Unknown source',
          ecmDriver: (['E', 'C', 'M'].includes(h.ecmDriver) ? h.ecmDriver : 'E') as 'E' | 'C' | 'M',
          controls: {
            eliminateSubstitute: '',
            engineering: '',
            adminTraining: '',
            ppe: ''
          },
          equipment: [],
          relevant: true
        })),
        affectedConditions: []
      }));
      const generalHazards: GeneralHazardItem[] = (parsed.generalHazards || []).map((h: any, i: number): GeneralHazardItem => ({
        id: `gh-ai-${i + 1}`,
        hazard: h.hazard || 'General hazard',
        source: h.source || 'Unknown source',
        ecmDriver: (['E', 'C', 'M'].includes(h.ecmDriver) ? h.ecmDriver : 'E') as 'E' | 'C' | 'M',
        controls: {
          eliminateSubstitute: '',
          engineering: '',
          adminTraining: '',
          ppe: ''
        },
        equipment: [],
        relevant: true
      }));
      setData(prev => ({
        ...prev,
        stepHazardData,
        generalHazards,
        generalEquipment: []
      }));
    } catch (err) {
      console.error('[AI Hazards List] Failed:', err);
      const errMsg = (err as any)?.message || String(err);
      setHazardGenError(`AI hazard generation failed: ${errMsg}`);
      const fallbackData: StepHazardData[] = data.jobSteps.map((step, i) => ({
        description: step,
        hazards: [{
          id: `h-fallback-${i}`,
          hazard: 'Identify hazard for this step',
          source: 'Assess the source of the hazard',
          ecmDriver: 'E' as const,
          controls: {
            eliminateSubstitute: '',
            engineering: '',
            adminTraining: '',
            ppe: ''
          },
          equipment: [],
          relevant: true
        }],
        affectedConditions: []
      }));
      setData(prev => ({
        ...prev,
        stepHazardData: fallbackData,
        generalHazards: [],
        generalEquipment: []
      }));
    }
  }, [data.jobSteps, data.taskDescription, data.environmentConditions]);
  // ── Phase 2: Generate controls & equipment for APPROVED hazards only ──
  const generateControls = useCallback(async () => {
    const apiKey = getSavedApiKey();
    const approvedStepHazards = data.stepHazardData.map(step => ({
      description: step.description,
      hazards: step.hazards.filter(h => h.relevant)
    }));
    const approvedGeneralHazards = data.generalHazards.filter(h => h.relevant);
    const totalApproved = approvedStepHazards.reduce((sum, s) => sum + s.hazards.length, 0) + approvedGeneralHazards.length;
    if (!apiKey || totalApproved === 0) {
      const updatedSteps = data.stepHazardData.map(step => ({
        ...step,
        hazards: step.hazards.map(h => h.relevant && !h.controls.eliminateSubstitute ? {
          ...h,
          controls: {
            eliminateSubstitute: 'Describe elimination or substitution',
            engineering: 'Describe engineering control',
            adminTraining: 'Describe administrative or training control',
            ppe: 'Standard PPE'
          }
        } : h)
      }));
      const updatedGeneral = data.generalHazards.map(h => h.relevant && !h.controls.eliminateSubstitute ? {
        ...h,
        controls: {
          eliminateSubstitute: 'Describe elimination or substitution',
          engineering: 'Describe engineering control',
          adminTraining: 'Describe administrative or training control',
          ppe: 'Standard PPE'
        }
      } : h);
      setData(prev => ({
        ...prev,
        stepHazardData: updatedSteps,
        generalHazards: updatedGeneral
      }));
      return;
    }
    try {
      const hazardList: {
        id: string;
        hazard: string;
        source: string;
        ecmDriver: string;
        stepDescription: string;
      }[] = [];
      for (const step of approvedStepHazards) {
        for (const h of step.hazards) {
          hazardList.push({
            id: h.id,
            hazard: h.hazard,
            source: h.source,
            ecmDriver: h.ecmDriver,
            stepDescription: step.description
          });
        }
      }
      for (const h of approvedGeneralHazards) {
        hazardList.push({
          id: h.id,
          hazard: h.hazard,
          source: h.source,
          ecmDriver: h.ecmDriver,
          stepDescription: 'General site hazard'
        });
      }
      const hazardListText = hazardList.map((h, i) => `${i + 1}. [${h.id}] "${h.hazard}" (source: ${h.source}, ECM: ${h.ecmDriver}, step: ${h.stepDescription})`).join('\n');
      const conditionsText = data.environmentConditions.length > 0 ? `\n\nRelevant Conditions:\n${data.environmentConditions.map(c => `- ${c}`).join('\n')}` : '';
      const completion = await chatCompletionWithRetry({
        model: 'gpt-4o-mini',
        messages: [{
          role: 'system',
          content: CONTROLS_GENERATION_PROMPT
        }, {
          role: 'user',
          content: `Task: ${data.taskDescription}\n\nMaster Equipment List:\n${(data.taskEquipment.length > 0 ? data.taskEquipment : data.equipment).map(e => `- ${e}`).join('\n')}\n\nApproved Hazards:\n${hazardListText}${conditionsText}`
        }],
        temperature: 0.3,
        max_tokens: 1500
      });
      const raw = completion.choices[0]?.message?.content?.trim() || '';
      let jsonStr = raw;
      const fenceMatch = raw.match(/```(?:json)?\s*([\s\S]*?)```/);
      if (fenceMatch) jsonStr = fenceMatch[1].trim();
      const parsed = JSON.parse(jsonStr);
      if (!parsed.hazardControls || !Array.isArray(parsed.hazardControls)) {
        throw new Error('Invalid response structure');
      }
      const controlsMap = new Map<string, {
        controls: {
          eliminateSubstitute: string;
          engineering: string;
          adminTraining: string;
          ppe: string;
        };
        equipment: string[];
      }>();
      for (const hc of parsed.hazardControls) {
        controlsMap.set(hc.id, {
          controls: {
            eliminateSubstitute: hc.controls?.eliminateSubstitute || 'Assess and eliminate',
            engineering: hc.controls?.engineering || 'Apply engineering controls',
            adminTraining: hc.controls?.adminTraining || 'Follow procedures',
            ppe: hc.controls?.ppe || 'Standard PPE'
          },
          equipment: Array.isArray(hc.equipment) ? hc.equipment : []
        });
      }
      const updatedSteps = data.stepHazardData.map(step => ({
        ...step,
        hazards: step.hazards.map(h => {
          const ctrl = controlsMap.get(h.id);
          if (ctrl && h.relevant) {
            return {
              ...h,
              controls: ctrl.controls,
              equipment: ctrl.equipment
            };
          }
          if (h.relevant && !h.controls.eliminateSubstitute) {
            return {
              ...h,
              controls: {
                eliminateSubstitute: 'Assess and eliminate',
                engineering: 'Apply engineering controls',
                adminTraining: 'Follow procedures',
                ppe: 'Standard PPE'
              }
            };
          }
          return h;
        })
      }));
      const updatedGeneral = data.generalHazards.map(h => {
        const ctrl = controlsMap.get(h.id);
        if (ctrl && h.relevant) {
          return {
            ...h,
            controls: ctrl.controls,
            equipment: ctrl.equipment
          };
        }
        if (h.relevant && !h.controls.eliminateSubstitute) {
          return {
            ...h,
            controls: {
              eliminateSubstitute: 'Assess and eliminate',
              engineering: 'Apply engineering controls',
              adminTraining: 'Follow procedures',
              ppe: 'Standard PPE'
            }
          };
        }
        return h;
      });
      const generalEquipment: string[] = Array.isArray(parsed.generalEquipment) ? parsed.generalEquipment : [];
      setData(prev => ({
        ...prev,
        stepHazardData: updatedSteps,
        generalHazards: updatedGeneral,
        generalEquipment
      }));
    } catch (err) {
      console.error('[AI Controls] Failed:', err);
      const updatedSteps = data.stepHazardData.map(step => ({
        ...step,
        hazards: step.hazards.map(h => h.relevant && !h.controls.eliminateSubstitute ? {
          ...h,
          controls: {
            eliminateSubstitute: 'Assess and eliminate',
            engineering: 'Apply engineering controls',
            adminTraining: 'Follow procedures',
            ppe: 'Standard PPE'
          }
        } : h)
      }));
      const updatedGeneral = data.generalHazards.map(h => h.relevant && !h.controls.eliminateSubstitute ? {
        ...h,
        controls: {
          eliminateSubstitute: 'Assess and eliminate',
          engineering: 'Apply engineering controls',
          adminTraining: 'Follow procedures',
          ppe: 'Standard PPE'
        }
      } : h);
      setData(prev => ({
        ...prev,
        stepHazardData: updatedSteps,
        generalHazards: updatedGeneral
      }));
    }
  }, [data.stepHazardData, data.generalHazards, data.environmentConditions, data.taskDescription]);
  // Sync status-bar CSS variables
  useEffect(() => {
    const root = document.documentElement;
    if (loading) {
      root.style.setProperty('--status-bar-bg', '#f9fafb');
      root.style.setProperty('--status-bar-pill', 'rgba(0,0,0,0.15)');
    } else {
      root.style.setProperty('--status-bar-bg', '#1C1C1E');
      root.style.setProperty('--status-bar-pill', 'rgba(255,255,255,0.3)');
    }
  }, [loading]);
  // Timer effect for recording
  useEffect(() => {
    if (currentStep !== recordingStep || isRecordingPaused) return;
    const interval = setInterval(() => {
      setRecordingSeconds(s => s + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [currentStep, recordingStep, isRecordingPaused]);
  // ── Prefetch conditions while user reads the intro screen (now step 7) ──
  useEffect(() => {
    if (currentStep !== 7) return;
    const existingAiConditions = (data._workConditionsCache || []).filter(c => !c.id.startsWith('cond-nearby'));
    if (existingAiConditions.length > 0) return;
    if (data.environmentConditions.length > 0) return;
    const apiKey = getSavedApiKey();
    const hasContext = data.taskDescription || data.jobSteps.length > 0;
    if (!apiKey || !hasContext) return;
    let cancelled = false;
    async function prefetch() {
      try {
        const jobStepsText = data.jobSteps.map((s, i) => `${i + 1}. ${s}`).join('\n');
        const completion = await chatCompletionWithRetry({
          model: 'gpt-4o-mini',
          messages: [{
            role: 'system',
            content: CONDITIONS_GENERATION_PROMPT
          }, {
            role: 'user',
            content: `Task: ${data.taskDescription}\n\nJob Steps:\n${jobStepsText}`
          }],
          temperature: 0.3,
          max_tokens: 500
        });
        if (cancelled) return;
        const raw = completion.choices[0]?.message?.content?.trim() || '';
        let jsonStr = raw;
        const fenceMatch = raw.match(/```(?:json)?\s*([\s\S]*?)```/);
        if (fenceMatch) jsonStr = fenceMatch[1].trim();
        const parsed = JSON.parse(jsonStr) as {
          conditions: {
            text: string;
            text_es?: string;
            category: 'E' | 'C' | 'M';
          }[];
        };
        if (!parsed.conditions || !Array.isArray(parsed.conditions)) return;
        if (cancelled) return;
        const aiConditions = parsed.conditions.map((c, i) => ({
          id: `cond-pre-${i + 1}`,
          text: c.text,
          text_es: c.text_es,
          relevant: null as boolean | null,
          category: c.category as 'E' | 'C' | 'M' | null
        }));
        setData(prev => {
          const existingAi = (prev._workConditionsCache || []).filter(c => !c.id.startsWith('cond-nearby'));
          if (existingAi.length > 0) return prev;
          const nearbyConditions = (prev._workConditionsCache || []).filter(c => c.id.startsWith('cond-nearby'));
          return {
            ...prev,
            _workConditionsCache: [...nearbyConditions, ...aiConditions]
          };
        });
      } catch (err) {
        console.error('[Prefetch Conditions] Failed:', err);
      }
    }
    prefetch();
    return () => {
      cancelled = true;
    };
  }, [currentStep, data.taskDescription, data.jobSteps, data.environmentConditions.length, lang]);
  // ── Merge pending nearby condition into cache ──
  useEffect(() => {
    if (!pendingNearbyCondition) return;
    setData(prev => {
      const alreadyExists = (prev._workConditionsCache || []).some(c => c.id === pendingNearbyCondition.id);
      if (alreadyExists) return prev;
      return {
        ...prev,
        _workConditionsCache: [pendingNearbyCondition, ...(prev._workConditionsCache || [])]
      };
    });
  }, [pendingNearbyCondition]);
  // ── Fetch nearby concurrent task alert when arriving at step 4 ──
  useEffect(() => {
    if (currentStep !== 4) return;
    if (nearbyTaskFetched) return;
    const apiKey = getSavedApiKey();
    if (!apiKey || !data.taskDescription) return;
    let cancelled = false;
    async function fetchNearbyTask() {
      try {
        const completion = await chatCompletionWithRetry({
          model: 'gpt-4o-mini',
          messages: [{
            role: 'system',
            content: NEARBY_TASK_PROMPT
          }, {
            role: 'user',
            content: data.taskDescription
          }],
          temperature: 0.5,
          max_tokens: 200
        });
        if (cancelled) return;
        const raw = completion.choices[0]?.message?.content?.trim() || '';
        let jsonStr = raw;
        const fenceMatch = raw.match(/```(?:json)?\s*([\s\S]*?)```/);
        if (fenceMatch) jsonStr = fenceMatch[1].trim();
        const parsed = JSON.parse(jsonStr) as {
          nearbyTask: string;
          location: string;
          concern: string;
        };
        if (parsed.nearbyTask && !cancelled) {
          setNearbyTaskAlert(parsed);
          setNearbyTaskFetched(true);
        }
      } catch (err) {
        console.error('[Nearby Task] Failed:', err);
        setNearbyTaskFetched(true);
      }
    }
    fetchNearbyTask();
    return () => {
      cancelled = true;
    };
  }, [currentStep, nearbyTaskFetched, data.taskDescription]);
  const updateData = (updates: Partial<PTPData>) => {
    setData(prev => ({
      ...prev,
      ...updates
    }));
  };
  const advanceStep = useCallback((toStep: number, dir: number) => {
    setDirection(dir);
    setCurrentStep(toStep);
  }, []);
  const handleNextWithLoading = useCallback((fromStep: number, toStep: number, dir: number) => {
    const transition = getLoadingTransition(fromStep);
    if (transition) {
      setLoading({
        message: transition.message,
        sub: transition.sub
      });
      setTimeout(() => {
        setLoading(null);
        advanceStep(toStep, dir);
      }, transition.duration);
    } else {
      advanceStep(toStep, dir);
    }
  }, [advanceStep, getLoadingTransition]);
  const handleNext = useCallback(() => {
    // Step 9 (Crew) → generate hazard LIST with AI, then go to step 10 (HazardReview)
    if (currentStep === 9) {
      setLoading({
        message: t('loading.identifyingHazards'),
        sub: t('loading.identifyingHazardsSub')
      });
      generateHazardsList().then(() => {
        setLoading(null);
        advanceStep(10, 1);
      });
      return;
    }
    // Step 10 (HazardReview) → generate controls for approved hazards
    if (currentStep === 10) {
      setLoading({
        message: t('loading.generatingControls'),
        sub: t('loading.generatingControlsSub')
      });
      generateControls().then(() => {
        setLoading(null);
        const firstRelevant = findNextRelevantStepScreen(10);
        if (firstRelevant !== null) {
          advanceStep(firstRelevant, 1);
        } else {
          advanceStep(10 + numHazardSteps + 1, 1);
        }
      });
      return;
    }
    // If currently on a step hazard screen, skip to next step with relevant hazards
    if (currentStep > 10 && currentStep <= 10 + numHazardSteps) {
      const nextRelevant = findNextRelevantStepScreen(currentStep);
      if (nextRelevant !== null) {
        handleNextWithLoading(currentStep, nextRelevant, 1);
      } else {
        handleNextWithLoading(currentStep, 10 + numHazardSteps + 1, 1);
      }
      return;
    }
    // After Recording stop, show New Info screen (only the first time)
    const recordingStopStep = 10 + numHazardSteps + 2 + 3;
    const newInfoStep = 10 + numHazardSteps + 2 + 6;
    const recordingCompleteStep = 10 + numHazardSteps + 2 + 4;
    if (currentStep === recordingStopStep && !hasShownNewInfo) {
      setHasShownNewInfo(true);
      handleNextWithLoading(currentStep, newInfoStep, 1);
      return;
    }
    if (currentStep === newInfoStep) {
      handleNextWithLoading(currentStep, recordingCompleteStep, 1);
      return;
    }
    const qualityScoreStep = 10 + numHazardSteps + 2 + 5;
    if (currentStep === qualityScoreStep) {
      handleNextWithLoading(currentStep, currentStep + 2, 1);
      return;
    }
    if (currentStep < TOTAL_STEPS) {
      handleNextWithLoading(currentStep, currentStep + 1, 1);
    }
  }, [currentStep, TOTAL_STEPS, handleNextWithLoading, generateHazardsList, generateControls, advanceStep, numHazardSteps, findNextRelevantStepScreen, hasShownNewInfo]);
  const handleBack = () => {
    if (currentStep > 1) {
      // If on a step hazard screen, skip back to previous step with relevant hazards
      if (currentStep > 10 && currentStep <= 10 + numHazardSteps) {
        const prevRelevant = findPrevRelevantStepScreen(currentStep);
        if (prevRelevant !== null) {
          setDirection(-1);
          setCurrentStep(prevRelevant);
        } else {
          setDirection(-1);
          setCurrentStep(10);
        }
        return;
      }
      // If on general hazards screen, skip back to last step with relevant hazards
      if (currentStep === 10 + numHazardSteps + 1) {
        const lastRelevant = findPrevRelevantStepScreen(10 + numHazardSteps + 1);
        if (lastRelevant !== null) {
          setDirection(-1);
          setCurrentStep(lastRelevant);
        } else {
          setDirection(-1);
          setCurrentStep(10);
        }
        return;
      }
      setDirection(-1);
      setCurrentStep(prev => prev - 1);
    }
  };
  const handleExport = (isUpdated = false) => {
    setExportIsUpdated(isUpdated);
    setShowExport(true);
  };
  const screenProps = {
    data,
    updateData,
    onNext: handleNext,
    onBack: handleBack,
    onExport: () => handleExport(currentStep >= 10 + numHazardSteps + 2 + 6)
  };
  const renderScreen = () => {
    if (currentStep === 1) return <Screen21_PTPList {...screenProps} onCardPress={ptp => {
      if (!ptp.meetingDone) {
        setDirection(1);
        setCurrentStep(10 + numHazardSteps + 2 + 2);
      } else if (!ptp.quizDone) {
        setDirection(1);
        setCurrentStep(10 + numHazardSteps + 2 + 9);
      } else {
        handleExport(true);
      }
    }} data-id="element-1903" />;
    if (currentStep === 2) return <NewPTPScreen {...screenProps} data-id="element-1904" />;
    if (currentStep === 3) return <VoiceDescriptionScreen {...screenProps} data-id="element-1905" />;
    if (currentStep === 4) return <TaskUnderstandingScreen {...screenProps} data-id="element-1906" />;
    if (currentStep === 5) return <EquipmentScreen {...screenProps} data-id="element-1907" />;
    if (currentStep === 6) return <PermitsScreen {...screenProps} data-id="element-1908" />;
    if (currentStep === 7) return <TaskConditionsIntroScreen {...screenProps} data-id="element-1909" />;
    if (currentStep === 8) return <WorkEnvironmentScreen {...screenProps} data-id="element-1910" />;
    if (currentStep === 9) return <CrewAssignmentScreen {...screenProps} data-id="element-1911" />;
    // Hazard Review Screen (step 10)
    if (currentStep === 10) {
      return <>
          {hazardGenError && <div className="mx-4 mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-id="element-1912">
              <p className="text-amber-800 text-xs font-medium mb-1" data-id="element-1913">
                ⚠️ {hazardGenError}
              </p>
              <button onClick={() => {
            setLoading({
              message: t('loading.identifyingHazards'),
              sub: t('loading.identifyingHazardsSub')
            });
            generateHazardsList().then(() => setLoading(null));
          }} className="text-amber-700 text-xs font-semibold underline" data-id="element-1914">
                Retry AI Generation
              </button>
            </div>}
          <HazardReviewScreen {...screenProps} stepHazardData={data.stepHazardData} generalHazards={data.generalHazards} onUpdateStepHazardData={newData => {
          updateData({
            stepHazardData: newData
          });
        }} onUpdateGeneralHazards={newHazards => {
          updateData({
            generalHazards: newHazards
          });
        }} data-id="element-1915" />
        </>;
    }
    // Dynamic Step Hazard Controls Screens (now starting at step 11)
    if (currentStep > 10 && currentStep <= 10 + numHazardSteps) {
      const hazardStepIndex = currentStep - 11;
      return <StepHazardScreen {...screenProps} stepIndex={hazardStepIndex} stepData={data.stepHazardData[hazardStepIndex]} conditions={data.environmentConditions} onUpdateStep={(idx, updatedStep) => {
        const newData = [...data.stepHazardData];
        newData[idx] = updatedStep;
        updateData({
          stepHazardData: newData
        });
      }} data-id="element-1916" />;
    }
    // General Hazards Controls
    if (currentStep === 10 + numHazardSteps + 1) {
      return <GeneralHazardsScreen {...screenProps} generalHazards={data.generalHazards} generalEquipment={data.generalEquipment} conditions={data.environmentConditions} onUpdateGeneral={(hazards, equipment) => {
        updateData({
          generalHazards: hazards,
          generalEquipment: equipment
        });
      }} data-id="element-1917" />;
    }
    // Summary
    if (currentStep === 10 + numHazardSteps + 2) {
      return <PTPSummaryScreen {...screenProps} data-id="element-1918" />;
    }
    // Post-Creation Flow
    const postCreationStep = currentStep - (10 + numHazardSteps + 2);
    switch (postCreationStep) {
      case 1:
        return <Screen10_PTPComplete {...screenProps} onBack={() => {
          setDirection(-1);
          setCurrentStep(1);
        }} data-id="element-1919" />;
      case 2:
        return <Screen11_PTPCreated {...screenProps} data-id="element-1920" />;
      case 3:
        return <Screen12_Recording {...screenProps} recordingSeconds={recordingSeconds} isRecordingPaused={isRecordingPaused} onTogglePause={() => setIsRecordingPaused(p => !p)} data-id="element-1921" />;
      case 4:
        return <Screen13_RecordingComplete {...screenProps} data-id="element-1922" />;
      case 5:
        return <Screen14_QualityScore {...screenProps} data-id="element-1923" />;
      case 6:
        return <Screen15_NewInfo {...screenProps} data-id="element-1924" />;
      case 7:
        return <Screen16_UpdatedPTP {...screenProps} onNext={() => {
          handleNextWithLoading(currentStep, currentStep + 2, 1);
        }} onBack={() => {
          setDirection(-1);
          setCurrentStep(1);
        }} data-id="element-1925" />;
      case 8:
        return <Screen17_FullReview {...screenProps} data-id="element-1926" />;
      case 9:
        return <Screen18_SendQuiz {...screenProps} data-id="element-1927" />;
      case 10:
        return <Screen19_ForemanQuiz {...screenProps} data-id="element-1928" />;
      case 11:
        return <Screen18_CrewQuizResults {...screenProps} onNext={() => {
          handleNextWithLoading(currentStep, currentStep + 1, 1);
        }} onBack={() => {
          setDirection(-1);
          setCurrentStep(currentStep - 4);
        }} data-id="element-1929" />;
      case 12:
        return <Screen21_PTPList {...screenProps} showNewPTP onNext={() => {
          setDirection(1);
          setCurrentStep(2);
        }} onCardPress={ptp => {
          if (!ptp.meetingDone) {
            setDirection(1);
            setCurrentStep(10 + numHazardSteps + 2 + 2);
          } else if (!ptp.quizDone) {
            setDirection(1);
            setCurrentStep(10 + numHazardSteps + 2 + 9);
          } else {
            handleExport(true);
          }
        }} data-id="element-1930" />;
      default:
        return null;
    }
  };
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction < 0 ? '100%' : '-100%',
      opacity: 0
    })
  };
  // Phase calculation — updated ranges
  let currentPhase = 0;
  let phaseLabel = '';
  if (currentStep >= 2 && currentStep <= 6) {
    currentPhase = 1;
    phaseLabel = t('phase.taskMop');
  } else if (currentStep >= 7 && currentStep <= 8) {
    currentPhase = 2;
    phaseLabel = t('phase.conditions');
  } else if (currentStep === 9) {
    currentPhase = 3;
    phaseLabel = t('phase.crew');
  } else if (currentStep === 10) {
    currentPhase = 4;
    phaseLabel = t('phase.hazards');
  } else if (currentStep >= 11 && currentStep <= 10 + numHazardSteps + 1) {
    currentPhase = 5;
    phaseLabel = t('phase.controls');
  } else if (currentStep === 10 + numHazardSteps + 2) {
    currentPhase = 6;
    phaseLabel = t('phase.finalization');
  }
  const headerTitle = currentStep === 1 ? t('list.title') || 'PTP List' : phaseLabel || 'BiltOn PTP';
  return <div className="w-full h-full flex flex-col bg-gray-50 text-gray-800 overflow-hidden relative font-sans isolate" data-id="element-1931">
      <AppHeader title={headerTitle} showBack={currentStep > 1 && currentStep < TOTAL_STEPS} onBack={handleBack} lang={lang} onToggleLang={() => setLang(lang === 'en' ? 'es' : 'en')} data-id="element-1932">
        <div className="px-5 pt-3" data-id="element-1933">
          <StepIndicator currentPhase={currentPhase} label={phaseLabel} data-id="element-1934" />
        </div>
      </AppHeader>

      <div className="flex-1 relative w-full h-full" data-id="element-1935">
        <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-1936">
          <motion.div key={currentStep} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          type: 'tween',
          ease: 'easeInOut',
          duration: 0.3
        }} className="absolute inset-0 w-full h-full overflow-y-auto overflow-x-hidden px-5 pb-4" data-id="element-1937">
            {renderScreen()}
          </motion.div>
        </AnimatePresence>
      </div>

      <AnimatePresence data-id="element-1938">
        {loading && <LoadingOverlay message={loading.message} subMessage={loading.sub} data-id="element-1939" />}
      </AnimatePresence>

      {/* Nearby Concurrent Task Alert Modal */}
      <AnimatePresence data-id="element-1940">
        {nearbyTaskAlert && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 z-[90] flex items-center justify-center bg-black/30 backdrop-blur-sm p-4" data-id="element-1941">
            <motion.div initial={{
          scale: 0.9,
          opacity: 0,
          y: 20
        }} animate={{
          scale: 1,
          opacity: 1,
          y: 0
        }} exit={{
          scale: 0.9,
          opacity: 0,
          y: 20
        }} transition={{
          type: 'spring',
          stiffness: 400,
          damping: 25
        }} className="bg-white border border-amber-300 rounded-2xl p-5 max-w-sm w-full shadow-2xl" data-id="element-1942">
              <div className="flex items-start gap-3 mb-4" data-id="element-1943">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-1944">
                  <AlertTriangleIcon className="w-5 h-5 text-amber-600" data-id="element-1945" />
                </div>
                <div data-id="element-1946">
                  <h3 className="text-gray-900 font-bold text-base mb-0.5" data-id="element-1947">
                    {t('nearby.title')}
                  </h3>
                  <p className="text-gray-500 text-xs" data-id="element-1948">
                    {t('nearby.subtitle')}
                  </p>
                </div>
                <button onClick={() => setNearbyTaskAlert(null)} className="ml-auto text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0" data-id="element-1949">
                  <XIcon className="w-4 h-4" data-id="element-1950" />
                </button>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 mb-4" data-id="element-1951">
                <p className="text-amber-800 text-sm font-medium mb-1.5" data-id="element-1952">
                  {nearbyTaskAlert.nearbyTask}
                </p>
                <p className="text-amber-600 text-xs mt-1" data-id="element-1953">
                  ⚠️ {nearbyTaskAlert.concern}
                </p>
              </div>
              <p className="text-gray-500 text-xs mb-4" data-id="element-1954">
                {t('nearby.willBeAdded')}
              </p>
              <div className="flex gap-2" data-id="element-1955">
                <button onClick={() => setNearbyTaskAlert(null)} className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-500 text-sm font-medium hover:bg-gray-100 transition-colors" data-id="element-1956">
                  {t('dismiss')}
                </button>
                <button onClick={() => {
              const conditionText = `Concurrent activity nearby: ${nearbyTaskAlert.nearbyTask}`;
              setPendingNearbyCondition({
                id: `cond-nearby-1`,
                text: conditionText,
                relevant: true,
                category: 'E'
              });
              setNearbyTaskAlert(null);
            }} className="flex-1 py-2.5 rounded-lg bg-amber-500 text-black text-sm font-bold hover:bg-amber-400 transition-colors" data-id="element-1957">
                  {t('nearby.addToConditions')}
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      <AnimatePresence data-id="element-1958">
        {showExport && <PTPExportView data={data} onClose={() => setShowExport(false)} isUpdated={exportIsUpdated} data-id="element-1959" />}
      </AnimatePresence>
    </div>;
}
export function PTPApp() {
  return <I18nProvider data-id="element-1960">
      <PTPAppInner data-id="element-1961" />
    </I18nProvider>;
}